import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useDriverMapSocket } from "../../../hooks/useDriverMapSocket";
import { MapDriver } from "../../../features/driverMap/driverMapSlice";
import { useSearchDriver } from "../../../apis/driverApis";
import { useFetchOFDOrderSummary } from "../../../apis/dashboardApis";
import { fetch3 } from "../../../commonApis/commonApis";
import { IconChevronLeft, IconChevronDown, IconLanguage, IconX, IconMaximize } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const url = process.env.REACT_APP_BASE_URL;

export interface TrackingMapProps {
  onSearchResults?: (results: MapDriver[], isSearching: boolean) => void;
}

export type MapLanguageOption = "en" | "ar" | "satellite";

export interface TileLayerConfig {
  name: string;
  label: string;
  url: string;
  attribution: string;
  subdomains?: string;
  maxZoom?: number;
}

export const TILE_LAYERS: Record<MapLanguageOption, TileLayerConfig> = {
  en: {
    name: "English",
    label: "English Map (Google)",
    url: "https://mt1.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}",
    attribution: "&copy; Google Maps",
    maxZoom: 20,
  },
  ar: {
    name: "العربية",
    label: "Arabic Map (Google)",
    url: "https://mt1.google.com/vt/lyrs=m&hl=ar&x={x}&y={y}&z={z}",
    attribution: "&copy; Google Maps",
    maxZoom: 20,
  },
  satellite: {
    name: "Satellite",
    label: "Satellite Imagery (English)",
    url: "https://mt1.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}",
    attribution: "&copy; Google Maps",
    maxZoom: 20,
  },
};

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export const formatGoogleLocation = (
  components: AddressComponent[],
): string => {
  const get = (type: string) =>
    components.find((c) => c.types.includes(type))?.long_name;

  const area = get("sublocality") || get("route") || get("landmark");
  const city = get("administrative_area_level_3") || get("locality");
  const pincode = get("postal_code");

  if (area || city)
    return `${area ?? ""}${area && city ? ", " : ""}${city ?? ""}`;

  if (pincode) return pincode;

  return "Location unavailable";
};

export const useLocationNameResolver = (language: string = "en") => {
  const locationCache = useRef<Map<string, string>>(new Map());
  const getLocationName = useCallback(
    async (lat: number, lon: number): Promise<string> => {
      const cacheKey = `${lat.toFixed(3)},${lon.toFixed(3)}:${language}`;
      if (locationCache.current.has(cacheKey)) {
        return locationCache.current.get(cacheKey)!;
      }
      const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
      try {
        if (GOOGLE_API_KEY) {
          const controller = new AbortController();
          const tid = setTimeout(() => controller.abort(), 2000);
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${GOOGLE_API_KEY}&language=${language}`,
            { signal: controller.signal }
          );
          clearTimeout(tid);
          const data = await response.json();
          if (data.status === "OK" && data.results?.length > 0) {
            const locationText = formatGoogleLocation(
              data.results[0].address_components,
            );
            locationCache.current.set(cacheKey, locationText);
            return locationText;
          }
        }
        // Free reverse geocoding fallback with 2-second timeout
        try {
          const controller = new AbortController();
          const tid = setTimeout(() => controller.abort(), 2000);
          const freeRes = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=${language}`,
            { signal: controller.signal }
          );
          clearTimeout(tid);
          if (freeRes.ok) {
            const data = await freeRes.json();
            const parts = [
              data.locality || data.city,
              data.principalSubdivision,
            ].filter(Boolean);
            if (parts.length > 0) {
              const text = parts.join(", ");
              locationCache.current.set(cacheKey, text);
              return text;
            }
          }
        } catch (e) {
          // Ignore network errors on fallback
        }

        const coords = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        locationCache.current.set(cacheKey, coords);
        return coords;
      } catch (error) {
        const coords = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
        locationCache.current.set(cacheKey, coords);
        return coords;
      }
    },
    [language],
  );
  return { getLocationName };
};

export function haversineDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface DriverFreshnessInfo {
  status: "LIVE" | "RECENT" | "STALE" | "OFFLINE";
  label: string;
  badgeClass: string;
  dotColor: string;
  dotClass: string;
  timeAgoText: string;
}

export function getDriverFreshness(
  driver: Partial<MapDriver> | null | undefined
): DriverFreshnessInfo {
  if (!driver) {
    return {
      status: "OFFLINE",
      label: "Offline",
      badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
      dotColor: "#94a3b8",
      dotClass: "bg-slate-400",
      timeAgoText: "Offline",
    };
  }

  let status: "LIVE" | "RECENT" | "STALE" | "OFFLINE" =
    driver.freshnessStatus || (driver.online ? "LIVE" : "OFFLINE");
  let timeAgoText = "";

  if (driver.lastSeen) {
    const diffSec = Math.max(
      0,
      (Date.now() - new Date(driver.lastSeen).getTime()) / 1000
    );
    if (!isNaN(diffSec)) {
      if (diffSec <= 35) {
        status = "LIVE";
        timeAgoText = diffSec < 5 ? "Just now" : `${Math.round(diffSec)}s ago`;
      } else if (diffSec <= 120) {
        status = "RECENT";
        timeAgoText = `${Math.round(diffSec)}s ago`;
      } else if (diffSec <= 900) {
        status = "STALE";
        timeAgoText = `${Math.round(diffSec / 60)}m ago`;
      } else {
        status = "OFFLINE";
        timeAgoText =
          diffSec < 3600
            ? `${Math.round(diffSec / 60)}m ago`
            : `${Math.round(diffSec / 3600)}h ago`;
      }
    }
  }

  switch (status) {
    case "LIVE":
      return {
        status: "LIVE",
        label: "Live (<30s)",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300",
        dotColor: "#22c55e",
        dotClass: "bg-emerald-500 animate-pulse",
        timeAgoText: timeAgoText || "Live (<30s)",
      };
    case "RECENT":
      return {
        status: "RECENT",
        label: "Recent (<2m)",
        badgeClass: "bg-sky-50 text-sky-700 border-sky-300",
        dotColor: "#0ea5e9",
        dotClass: "bg-sky-500",
        timeAgoText: timeAgoText || "Recent (<2m)",
      };
    case "STALE":
      return {
        status: "STALE",
        label: "Stale (2-15m)",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-300",
        dotColor: "#f59e0b",
        dotClass: "bg-amber-500",
        timeAgoText: timeAgoText || "Stale (2-15m)",
      };
    case "OFFLINE":
    default:
      return {
        status: "OFFLINE",
        label: "Offline (>15m)",
        badgeClass: "bg-slate-100 text-slate-600 border-slate-200",
        dotColor: "#94a3b8",
        dotClass: "bg-slate-400",
        timeAgoText: timeAgoText || "Offline (>15m)",
      };
  }
}

export function calculateBearing(
  startLat: number,
  startLng: number,
  destLat: number,
  destLng: number
): number {
  const startLatRad = (startLat * Math.PI) / 180;
  const startLngRad = (startLng * Math.PI) / 180;
  const destLatRad = (destLat * Math.PI) / 180;
  const destLngRad = (destLng * Math.PI) / 180;

  const y = Math.sin(destLngRad - startLngRad) * Math.cos(destLatRad);
  const x =
    Math.cos(startLatRad) * Math.sin(destLatRad) -
    Math.sin(startLatRad) * Math.cos(destLatRad) * Math.cos(destLngRad - startLngRad);
  let brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

export function getShortestAngle(current: number, target: number): number {
  let diff = (target - current) % 360;
  if (diff < -180) diff += 360;
  if (diff > 180) diff -= 360;
  return current + diff;
}

export function slideMarker(
  marker: L.Marker,
  from: [number, number],
  to: [number, number],
  duration: number = 5000,
  onComplete?: () => void
) {
  if ((marker as any)._slideAnimId) {
    cancelAnimationFrame((marker as any)._slideAnimId);
    (marker as any)._slideAnimId = null;
  }

  const dLat = to[0] - from[0];
  const dLng = to[1] - from[1];
  const dist = Math.hypot(dLat, dLng);

  if (dist < 0.000002) {
    marker.setLatLng(to);
    if (onComplete) onComplete();
    return;
  }

  // Exact velocity per millisecond along latitude and longitude
  const vLat = dLat / duration;
  const vLng = dLng / duration;
  const startTime = performance.now();

  // Short buffer (Dead Reckoning) for normal network jitter (1.5 seconds)
  // Keeps smooth speed while waiting for next 5s packet, without drifting off into distance
  const maxExtrapolationTime = 1500;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;

    if (elapsed <= duration) {
      // Phase 1: Constant-speed linear glide directly towards target
      const progress = elapsed / duration;
      const lat = from[0] + dLat * progress;
      const lng = from[1] + dLng * progress;
      marker.setLatLng([lat, lng]);
      (marker as any)._slideAnimId = requestAnimationFrame(animate);
    } else {
      // Phase 2: Short glide buffer for packet jitter
      const extraTime = elapsed - duration;
      if (extraTime < maxExtrapolationTime) {
        const extLat = to[0] + vLat * extraTime;
        const extLng = to[1] + vLng * extraTime;
        marker.setLatLng([extLat, extLng]);
        (marker as any)._slideAnimId = requestAnimationFrame(animate);
      } else {
        (marker as any)._slideAnimId = null;
        // Snap cleanly to target coordinate when window ends
        marker.setLatLng(to);
        if (onComplete) onComplete();
      }
    }
  };

  (marker as any)._slideAnimId = requestAnimationFrame(animate);
}

export const createDriverIcon = (
  driver: MapDriver,
  isSelected: boolean,
  headingDeg: number
) => {
  const freshness = getDriverFreshness(driver);
  const labelText = driver.code || driver.name || "Driver";
  const hasDirection =
    driver.direction != null &&
    !isNaN(Number(driver.direction)) &&
    Number(driver.direction) !== 0;
  const directionDeg = hasDirection ? Number(driver.direction) : headingDeg;
  const statusClass = freshness.status.toLowerCase();

  return L.divIcon({
    className: "driver-marker-wrapper",
    html: `
      <div class="driver-marker-node ${isSelected ? "is-selected" : ""}">
        <div class="driver-marker-badge" title="${labelText} • ${freshness.label} (${freshness.timeAgoText})">
          <span class="status-dot ${statusClass}" style="background-color: ${freshness.dotColor};"></span>
          <span class="badge-text">${labelText}</span>
        </div>
        <div class="driver-car-container" style="transform: rotate(${directionDeg}deg);">
          <img src="/img/driver-car.png" alt="car" class="driver-car-img" />
        </div>
      </div>
    `,
    iconSize: [64, 64],
    iconAnchor: [32, 32],
    popupAnchor: [0, -32],
  });
};

// Persistent driver avatar cache to preserve profile pictures across search & location updates
const driverAvatarCache = new Map<string, string>();

export const saveDriverAvatar = (driver: Partial<MapDriver> | null | undefined) => {
  if (!driver) return;
  const img = driver.image;
  if (
    img &&
    typeof img === "string" &&
    (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:"))
  ) {
    const id = driver.id ? String(driver.id) : "";
    const code = driver.code ? String(driver.code) : "";
    if (id) {
      driverAvatarCache.set(id, img);
      try {
        localStorage.setItem(`tracktech_avatar_id_${id}`, img);
      } catch (_) {}
    }
    if (code) {
      driverAvatarCache.set(code, img);
      try {
        localStorage.setItem(`tracktech_avatar_code_${code}`, img);
      } catch (_) {}
    }
  }
};

export const getDriverAvatar = (driver: Partial<MapDriver> | null | undefined): string => {
  if (!driver) return "";
  if (
    driver.image &&
    typeof driver.image === "string" &&
    (driver.image.startsWith("http://") || driver.image.startsWith("https://") || driver.image.startsWith("data:"))
  ) {
    saveDriverAvatar(driver);
    return driver.image;
  }
  const id = driver.id ? String(driver.id) : "";
  const code = driver.code ? String(driver.code) : "";
  if (id && driverAvatarCache.has(id)) return driverAvatarCache.get(id)!;
  if (code && driverAvatarCache.has(code)) return driverAvatarCache.get(code)!;

  try {
    if (id) {
      const stored = localStorage.getItem(`tracktech_avatar_id_${id}`);
      if (stored && (stored.startsWith("http") || stored.startsWith("data:"))) {
        driverAvatarCache.set(id, stored);
        return stored;
      }
    }
    if (code) {
      const stored = localStorage.getItem(`tracktech_avatar_code_${code}`);
      if (stored && (stored.startsWith("http") || stored.startsWith("data:"))) {
        driverAvatarCache.set(code, stored);
        return stored;
      }
    }
  } catch (_) {}

  return "";
};

export default function TrackingMap({ onSearchResults }: TrackingMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const driversGroupRef = useRef<L.FeatureGroup | null>(null);
  const routeGroupRef = useRef<L.FeatureGroup | null>(null);
  const shipmentsGroupRef = useRef<L.FeatureGroup | null>(null);
  const highlightCircleRef = useRef<L.Circle | null>(null);
  const driverMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const driverHeadingsRef = useRef<Map<string, number>>(new Map());
  const initialFitDoneRef = useRef<boolean>(false);
  const lastSearchQueryRef = useRef<string>("");
  const lastPanTimeRef = useRef<number>(0);
  const lastFetchedRouteDriverIdRef = useRef<string | null>(null);
  const initialEnrichDoneRef = useRef<boolean>(false);

  const [language, setLanguage] = useState<MapLanguageOption>("en");
  const [langDropdownOpen, setLangDropdownOpen] = useState<boolean>(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MapDriver[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSelectionLocked, setIsSelectionLocked] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState<string | null>(null);
  const [, setSearchParams] = useSearchParams();
  const handleDriverMarkerClickRef = useRef<(driver: MapDriver) => void>(() => {});

  const geocodeLang = language === "ar" ? "ar" : "en";
  const { getLocationName } = useLocationNameResolver(geocodeLang);
  const [driversToDisplay, setDriversToDisplay] = useState<MapDriver[]>([]);

  const fitAllDrivers = useCallback(() => {
    if (!map.current || driversToDisplay.length === 0) return;
    const bounds = L.latLngBounds([]);
    let hasValidPositions = false;
    driversToDisplay.forEach((driver) => {
      if (driver.lat && driver.lon && driver.lat !== 0 && driver.lon !== 0) {
        bounds.extend([driver.lat, driver.lon]);
        hasValidPositions = true;
      }
    });
    if (hasValidPositions && bounds.isValid()) {
      map.current.fitBounds(bounds, { padding: [80, 80], maxZoom: 13 });
    }
  }, [driversToDisplay]);

  const { data: ofdData } = useFetchOFDOrderSummary({
    type: "Today",
  });

  const {
    drivers,
    loading: isLoading,
    error,
    socketConnected,
  } = useSelector((state: RootState) => state.driverMap);

  const { requestDriverData } = useDriverMapSocket();
  const { data: searchApiResults, isLoading: searchLoading } = useSearchDriver(
    searchQuery.trim(),
  );

  const [selectedDriver, setSelectedDriver] = useState<MapDriver | null>(null);
  const [showDriverPanel, setShowDriverPanel] = useState(false);
  const [routeData, setRouteData] = useState<any>(null);
  const [avatarError, setAvatarError] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<
    ("LIVE" | "RECENT" | "STALE" | "OFFLINE")[]
  >(["LIVE", "RECENT", "STALE"]);

  const toggleStatusFilter = (
    status: "LIVE" | "RECENT" | "STALE" | "OFFLINE"
  ) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const isAllStatusesSelected =
    selectedStatuses.length === 4 &&
    selectedStatuses.includes("LIVE") &&
    selectedStatuses.includes("RECENT") &&
    selectedStatuses.includes("STALE") &&
    selectedStatuses.includes("OFFLINE");

  // 1-second clock ticker to update relative times (e.g. 72s ago), freshness badges, and counts in real time
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setClockTick((prev) => (prev + 1) % 10000);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const driverStatusCounts = useMemo(() => {
    // Reference clockTick so freshness recalculates live every second as pings age
    if (clockTick < 0) return { LIVE: 0, RECENT: 0, STALE: 0, OFFLINE: 0, ACTIVE: 0 };
    const counts = { LIVE: 0, RECENT: 0, STALE: 0, OFFLINE: 0, ACTIVE: 0 };
    (drivers || []).forEach((d) => {
      const f = getDriverFreshness(d);
      counts[f.status]++;
    });
    counts.ACTIVE = counts.LIVE + counts.RECENT + counts.STALE;
    return counts;
  }, [drivers, clockTick]);

  // Periodic update of driver marker status badges and cancellation of stale animations
  useEffect(() => {
    driverMarkersRef.current.forEach((marker) => {
      const d = (marker as any)._driver;
      if (!d) return;
      const freshness = getDriverFreshness(d);

      // If driver is no longer LIVE, stop any lingering slide animation and snap to exact coords
      if (freshness.status !== "LIVE" && (marker as any)._slideAnimId) {
        cancelAnimationFrame((marker as any)._slideAnimId);
        (marker as any)._slideAnimId = null;
        if (d.lat && d.lon) {
          marker.setLatLng([d.lat, d.lon]);
        }
      }

      const el = marker.getElement();
      if (el) {
        const dot = el.querySelector(".status-dot") as HTMLElement | null;
        if (dot) {
          dot.className = `status-dot ${freshness.status.toLowerCase()}`;
          dot.style.backgroundColor = freshness.dotColor;
        }
        const badge = el.querySelector(".driver-marker-badge") as HTMLElement | null;
        if (badge) {
          badge.title = `${d.code || d.name || "Driver"} • ${freshness.label} (${freshness.timeAgoText})`;
        }
      }
    });
  }, [clockTick]);

  // Cache avatars from initial drivers & search results
  useEffect(() => {
    drivers?.forEach((d) => saveDriverAvatar(d));
  }, [drivers]);

  useEffect(() => {
    searchResults?.forEach((d) => saveDriverAvatar(d));
  }, [searchResults]);

  useEffect(() => {
    if (Array.isArray(searchApiResults)) {
      searchApiResults.forEach((d: any) => saveDriverAvatar(d));
    }
  }, [searchApiResults]);

  useEffect(() => {
    if (selectedDriver) {
      saveDriverAvatar(selectedDriver);
      setAvatarError(false);
    }
  }, [selectedDriver?.id, selectedDriver?.code, selectedDriver?.image]);

  const packagePendingOFDIcon = "/img/ofd-icon.svg";
  const packageNotDeliveredIcon = "/img/n-dlvd-icon.svg";
  const packageDeliveredIcon = "/img/dlvd-icon.svg";

  const handleSearch = (query: string) => {
    setIsSelectionLocked(false);
    setSearchQuery(query);
    setSelectedIndex(-1);
    if (!query.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      setShowDropdown(false);
      setSelectedDriver(null);
      setShowDriverPanel(false);
      setRouteData(null);
      clearRouteOverlays();
      return;
    }
    setIsSearching(true);
    setShowDropdown(true);
    setSearchResults([]);
  };

  useEffect(() => {
    if (searchApiResults && searchQuery.trim() && isSearching) {
      if (Array.isArray(searchApiResults) && searchApiResults.length > 0) {
        const processDrivers = async () => {
          const transformedResults: MapDriver[] = await Promise.all(
            searchApiResults?.map(async (driver: any) => {
              const lat = parseFloat(driver?.latitude) || 0;
              const lon = parseFloat(driver?.longitude) || 0;
              let currentLocation = "Unknown";
              if (lat !== 0 && lon !== 0) {
                currentLocation = await getLocationName(lat, lon);
              }
              const avatar =
                getDriverAvatar(driver) ||
                (driver?.image && typeof driver?.image === "string" && driver?.image.startsWith("http")
                  ? driver?.image
                  : "");
              if (avatar) {
                saveDriverAvatar({ id: driver?.id, code: driver?.code, image: avatar });
              }
              return {
                id: driver?.id || "",
                name: driver?.name || "Unknown Driver",
                code: driver?.code || "",
                image: avatar,
                latitude: driver?.latitude?.toString() || "0",
                longitude: driver?.longitude?.toString() || "0",
                currentLocation,
                online: driver?.online !== false,
                lat,
                lon,
                totalShipments:
                  parseInt(driver?.totalShipments?.toString()) || 0,
                outForDelivery:
                  parseInt(driver?.outForDelivery?.toString()) || 0,
                ordersDelivered:
                  parseInt(driver?.ordersDelivered?.toString()) || 0,
                ordersNotDelivered:
                  parseInt(driver?.ordersNotDelivered?.toString()) || 0,
              };
            }),
          );
          setSearchResults(transformedResults);
        };
        processDrivers();
      } else {
        setSearchResults([]);
      }
    }
    if (!searchQuery.trim() || !isSearching) {
      setSearchResults([]);
    }
    if (
      isSearching &&
      searchQuery?.trim() &&
      (!searchApiResults ||
        (Array.isArray(searchApiResults) && searchApiResults?.length === 0))
    ) {
      const localResults = drivers.filter(
        (driver) =>
          driver?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          driver?.code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          (driver?.currentLocation &&
            driver?.currentLocation
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase())),
      );
      if (localResults.length > 0) {
        setSearchResults(localResults);
      }
    }
  }, [
    searchApiResults,
    searchQuery,
    isSearching,
    getLocationName,
    drivers,
    language,
  ]);

  // Keep selectedDriver in sync with latest data from Redux drivers
  useEffect(() => {
    if (selectedDriver) {
      const updated = drivers.find(
        (d) => d.id === selectedDriver.id || (d.code && d.code === selectedDriver.code)
      );
      if (
        updated &&
        (updated.lat !== selectedDriver.lat ||
          updated.lon !== selectedDriver.lon ||
          updated.freshnessStatus !== selectedDriver.freshnessStatus ||
          updated.speed !== selectedDriver.speed ||
          updated.direction !== selectedDriver.direction ||
          updated.lastSeen !== selectedDriver.lastSeen)
      ) {
        setSelectedDriver((prev) => {
          if (!prev) return updated;
          const bestAvatar =
            getDriverAvatar(updated) ||
            getDriverAvatar(prev) ||
            (prev.image && prev.image.startsWith("http") ? prev.image : "") ||
            (updated.image && updated.image.startsWith("http") ? updated.image : "");
          return {
            ...prev,
            ...updated,
            image: bestAvatar || prev.image || updated.image || "",
            currentLocation: prev.currentLocation || updated.currentLocation || "Jaipur",
          };
        });
      }
    }
  }, [drivers, selectedDriver?.id, selectedDriver?.code]);

  useEffect(() => {
    let baseList = drivers;
    if (!isAllStatusesSelected) {
      baseList = baseList.filter((d) =>
        selectedStatuses.includes(getDriverFreshness(d).status)
      );
    }
    if (isSearching && searchQuery.trim() && searchResults.length > 0) {
      baseList = searchResults;
    }
    if (selectedDriver) {
      const exists = baseList.some(
        (d) => d.id === selectedDriver.id || (d.code && d.code === selectedDriver.code)
      );
      if (!exists && selectedDriver.lat && selectedDriver.lon && selectedDriver.lat !== 0) {
        baseList = [selectedDriver, ...baseList];
      }
    }
    setDriversToDisplay(baseList);
  }, [
    drivers,
    searchResults,
    isSearching,
    searchQuery,
    selectedDriver,
    selectedStatuses,
    isAllStatusesSelected,
    clockTick,
  ]);

  // Geocode address on initial load once only to prevent network bottlenecks during location updates
  useEffect(() => {
    if (isLoading || drivers.length === 0 || initialEnrichDoneRef.current) return;
    initialEnrichDoneRef.current = true;

    const enrichDrivers = async () => {
      try {
        const enriched = await Promise.all(
          drivers?.map(async (driver) => {
            const lat = driver.lat;
            const lon = driver.lon;
            const current = driver.currentLocation?.trim();
            const shouldEnrich =
              lat &&
              lon &&
              lat !== 0 &&
              lon !== 0 &&
              (!current ||
                current === "Unknown" ||
                current.length < 20 ||
                current.split(",").length <= 2 ||
                /^\d+\.\d+/.test(current) ||
                /^[A-Za-z\s]+$/.test(current));
            if (shouldEnrich) {
              const fullAddress = await getLocationName(lat, lon);
              return { ...driver, currentLocation: fullAddress };
            }
            return driver;
          }),
        );
        if (!isSearching) {
          if (isSelectionLocked && selectedDriver) {
            const currentSelected = enriched.find(
              (d) => d.id === selectedDriver.id || (d.code && d.code === selectedDriver.code)
            );
            setDriversToDisplay(currentSelected ? [currentSelected] : [selectedDriver]);
          } else {
            let baseList = enriched;
            if (!isAllStatusesSelected) {
              baseList = baseList.filter((d) =>
                selectedStatuses.includes(getDriverFreshness(d).status)
              );
            }
            setDriversToDisplay(baseList);
          }
        }
      } catch (err) {
        console.warn("Initial enrichment error:", err);
      }
    };
    enrichDrivers();
  }, [drivers, isLoading, getLocationName, isSearching, isSelectionLocked, selectedDriver, selectedStatuses, isAllStatusesSelected]);

  // When driver is selected and panel is open, resolve its address on-demand
  useEffect(() => {
    if (!selectedDriver || !showDriverPanel || !selectedDriver.lat || !selectedDriver.lon) return;
    const current = selectedDriver.currentLocation?.trim();
    if (!current || current === "Unknown" || /^\d+\.\d+/.test(current)) {
      getLocationName(selectedDriver.lat, selectedDriver.lon).then((address) => {
        if (address) {
          setSelectedDriver((prev) => (prev ? { ...prev, currentLocation: address } : null));
        }
      });
    }
  }, [selectedDriver?.id, showDriverPanel, getLocationName]);

  useEffect(() => {
    if (onSearchResults) {
      onSearchResults(
        driversToDisplay,
        isSearching && searchQuery.trim().length > 0,
      );
    }
  }, [driversToDisplay, isSearching, searchQuery, onSearchResults]);

  const showHighlightCircle = (lat: number, lon: number) => {
    if (!map.current) return;
    if (highlightCircleRef.current) {
      try {
        if (map.current.hasLayer(highlightCircleRef.current)) {
          map.current.removeLayer(highlightCircleRef.current);
        }
      } catch (e) {}
      highlightCircleRef.current = null;
    }

    const circle = L.circle([lat, lon], {
      color: "#3b82f6",
      weight: 2,
      opacity: 0.8,
      fillColor: "#3b82f6",
      fillOpacity: 0.25,
      radius: 80,
    }).addTo(map.current);

    highlightCircleRef.current = circle;

    setTimeout(() => {
      if (map.current && highlightCircleRef.current === circle) {
        try {
          if (map.current.hasLayer(circle)) {
            map.current.removeLayer(circle);
          }
        } catch (e) {}
        highlightCircleRef.current = null;
      }
    }, 6000);
  };

  const goToDriverLocation = (driver: MapDriver, openPanel: boolean = true) => {
    saveDriverAvatar(driver);
    const avatar =
      getDriverAvatar(driver) ||
      (driver.image && typeof driver.image === "string" && driver.image.startsWith("http")
        ? driver.image
        : "");
    const fullDriver: MapDriver = { ...driver, image: avatar };
    setSelectedDriver(fullDriver);
    if (openPanel) {
      setShowDriverPanel(true);
    }
    clearRouteOverlays();

    const lat = Number(fullDriver.lat || (fullDriver as any).latitude);
    const lon = Number(fullDriver.lon || (fullDriver as any).longitude);

    if (map.current && mapReady && !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
      map.current.setView([lat, lon], 15, { animate: true });
      showHighlightCircle(lat, lon);
    }
  };

  const handleDriverSelectFromSearch = (driver: MapDriver) => {
    saveDriverAvatar(driver);
    const avatar =
      getDriverAvatar(driver) ||
      (driver.image && typeof driver.image === "string" && driver.image.startsWith("http")
        ? driver.image
        : "");

    // Look for matching live driver from Redux drivers to ensure real-time coordinates
    const liveMatch = drivers.find(
      (d) => String(d.id) === String(driver.id) || (d.code && d.code === driver.code)
    );

    const lat = liveMatch?.lat && liveMatch.lat !== 0 ? liveMatch.lat : (driver.lat || parseFloat(driver.latitude) || 0);
    const lon = liveMatch?.lon && liveMatch.lon !== 0 ? liveMatch.lon : (driver.lon || parseFloat(driver.longitude) || 0);

    const fullDriver: MapDriver = {
      ...driver,
      ...(liveMatch || {}),
      image: avatar || liveMatch?.image || driver.image || "",
      lat,
      lon,
      latitude: String(lat),
      longitude: String(lon),
    };

    setIsSelectionLocked(true);
    setSearchQuery(fullDriver.name || fullDriver.code || "");
    setIsSearching(false);
    setShowDropdown(false);
    setSelectedIndex(-1);
    setSelectedDriver(fullDriver);
    setShowDriverPanel(true);
    goToDriverLocation(fullDriver, true);
  };

  const handleDriverMarkerClick = (driver: MapDriver) => {
    saveDriverAvatar(driver);
    const avatar =
      getDriverAvatar(driver) ||
      (driver.image && typeof driver.image === "string" && driver.image.startsWith("http")
        ? driver.image
        : "");
    const fullDriver = { ...driver, image: avatar };
    setIsSelectionLocked(true);
    setSelectedDriver(fullDriver);
    setShowDriverPanel(true);

    const lat = Number(fullDriver.lat || (fullDriver as any).latitude);
    const lon = Number(fullDriver.lon || (fullDriver as any).longitude);
    if (map.current && mapReady && !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
      map.current.setView([lat, lon], 15, { animate: true });
      showHighlightCircle(lat, lon);
    }
  };

  useEffect(() => {
    handleDriverMarkerClickRef.current = handleDriverMarkerClick;
  });

  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setSearchResults([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    setSelectedDriver(null);
    setShowDriverPanel(false);
    setIsSelectionLocked(false);
    lastSearchQueryRef.current = "";
    setRouteData(null);
    clearRouteOverlays();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || searchResults.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (searchResults.length === 0) return;
        const indexToUse =
          selectedIndex >= 0 && selectedIndex < searchResults.length
            ? selectedIndex
            : 0;
        handleDriverSelectFromSearch(searchResults[indexToUse]);
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    requestDriverData();
    const timer = setInterval(() => {
      requestDriverData();
    }, 10000);
    return () => clearInterval(timer);
  }, [requestDriverData]);

  // Handle click outside for language / layer dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      const mapInstance = L.map(mapContainer.current, {
        center: [23.8859, 45.0792],
        zoom: 11,
        zoomControl: false,
        attributionControl: false,
        zoomAnimation: true,
        zoomSnap: 0.5,
        zoomDelta: 0.5,
        wheelPxPerZoomLevel: 120,
      });

      L.control.zoom({ position: "bottomright" }).addTo(mapInstance);

      const config = TILE_LAYERS[language] || TILE_LAYERS.en;
      const initialTileLayer = L.tileLayer(config.url, {
        maxZoom: config.maxZoom || 20,
        maxNativeZoom: 20,
        attribution: config.attribution,
        subdomains: config.subdomains || "abc",
        keepBuffer: 8,
        updateWhenZooming: false,
        updateInterval: 100,
      }).addTo(mapInstance);

      tileLayerRef.current = initialTileLayer;

      driversGroupRef.current = L.featureGroup().addTo(mapInstance);
      routeGroupRef.current = L.featureGroup().addTo(mapInstance);
      shipmentsGroupRef.current = L.featureGroup().addTo(mapInstance);

      map.current = mapInstance;
      setMapReady(true);
      setMapLoadError(null);

      setTimeout(() => {
        mapInstance.invalidateSize();
      }, 200);
    } catch (e: any) {
      console.error("Leaflet Map Load Error:", e);
      setMapLoadError("Failed to initialize map.");
    }

    return () => {
      if (map.current) {
        try {
          map.current.stop();
          map.current.off();
          map.current.remove();
        } catch (err) {}
        map.current = null;
      }
      setMapReady(false);
    };
  }, []);

  // Switch map language / tile layer dynamically
  useEffect(() => {
    if (!map.current) return;

    if (tileLayerRef.current) {
      try {
        map.current.removeLayer(tileLayerRef.current);
      } catch (e) {}
    }

    const config = TILE_LAYERS[language] || TILE_LAYERS.en;
    const newTileLayer = L.tileLayer(config.url, {
      maxZoom: config.maxZoom || 20,
      maxNativeZoom: 20,
      attribution: config.attribution,
      subdomains: config.subdomains || "abc",
      keepBuffer: 8,
      updateWhenZooming: false,
      updateInterval: 100,
    }).addTo(map.current);

    tileLayerRef.current = newTileLayer;
  }, [language]);

  // Handle window resizing
  useEffect(() => {
    const handleResize = () => {
      map.current?.invalidateSize();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-fit bounds to show all drivers: ONLY on initial load or on new search
  useEffect(() => {
    if (
      !map.current ||
      !mapReady ||
      driversToDisplay.length === 0 ||
      isSelectionLocked ||
      selectedDriver
    )
      return;

    const currentSearch = searchQuery.trim();
    const isNewSearch =
      isSearching &&
      currentSearch.length > 0 &&
      lastSearchQueryRef.current !== currentSearch;

    // Do NOT reset map if already fitted initially and not a new search
    if (initialFitDoneRef.current && !isNewSearch) {
      return;
    }

    const bounds = L.latLngBounds([]);
    let hasValidPositions = false;

    driversToDisplay.forEach((driver) => {
      if (driver.lat && driver.lon && driver.lat !== 0 && driver.lon !== 0) {
        bounds.extend([driver.lat, driver.lon]);
        hasValidPositions = true;
      }
    });

    if (hasValidPositions && bounds.isValid()) {
      map.current.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 13,
      });

      initialFitDoneRef.current = true;
      if (isSearching) {
        lastSearchQueryRef.current = currentSearch;
      }
    }
  }, [
    driversToDisplay,
    mapReady,
    isSelectionLocked,
    selectedDriver,
    isSearching,
    searchQuery,
  ]);

  // Render All Drivers as Trucks with Smooth Sliding Animation
  useEffect(() => {
    if (!map.current || !mapReady || !driversGroupRef.current) return;

    const currentKeys = new Set<string>();

    driversToDisplay.forEach((driver) => {
      const key = driver.id || driver.code;
      if (!key || !driver.lat || !driver.lon || driver.lat === 0 || driver.lon === 0)
        return;

      currentKeys.add(key);
      const isSelected = selectedDriver?.id === driver.id || (selectedDriver?.code && selectedDriver?.code === driver.code);
      let marker = driverMarkersRef.current.get(key);

      if (!marker) {
        // Create new marker
        const heading =
          driver.direction != null && !isNaN(Number(driver.direction))
            ? Number(driver.direction)
            : 0;
        driverHeadingsRef.current.set(key, heading);

        marker = L.marker([driver.lat, driver.lon], {
          icon: createDriverIcon(driver, !!isSelected, heading),
          zIndexOffset: isSelected ? 1500 : 1000,
        });
        (marker as any)._driver = driver;
        (marker as any)._currentZIndex = isSelected ? 1500 : 1000;
        (marker as any)._lastUpdateTime = performance.now();
        (marker as any)._lastCoords = [driver.lat, driver.lon];
        (marker as any)._lastFreshnessStatus = getDriverFreshness(driver).status;
        const initialSpeedMps =
          driver.speed != null && !isNaN(Number(driver.speed)) && Number(driver.speed) > 0
            ? (Number(driver.speed) * 1000) / 3600
            : 10.5;
        (marker as any)._smoothedSpeed = initialSpeedMps;

        marker.on("click", (e) => {
          if (e && (e as any).originalEvent) {
            L.DomEvent.stopPropagation((e as any).originalEvent);
          }
          handleDriverMarkerClickRef.current((marker as any)._driver || driver);
        });

        driversGroupRef.current?.addLayer(marker);
        driverMarkersRef.current.set(key, marker);
      } else {
        // Existing marker - update position
        (marker as any)._driver = driver;
        const currentPos = marker.getLatLng();
        const targetLat = driver.lat;
        const targetLon = driver.lon;

        // Ground distance in meters from current car position to new target
        const remainingDistance = haversineDistanceMeters(
          currentPos.lat,
          currentPos.lng,
          targetLat,
          targetLon
        );

        const freshness = getDriverFreshness(driver);
        const isNonLiveStatus = freshness.status !== "LIVE"; // RECENT, STALE, or OFFLINE
        const prevFreshnessStatus = (marker as any)._lastFreshnessStatus;
        const wasNonLive = prevFreshnessStatus && prevFreshnessStatus !== "LIVE";

        const now = performance.now();
        const lastUpdate = (marker as any)._lastUpdateTime;
        const lastCoords = (marker as any)._lastCoords;
        const timeSinceLastUpdateMs = lastUpdate ? (now - lastUpdate) : Infinity;

        let pingAgeSec = 0;
        if (driver.lastSeen) {
          const parsed = new Date(driver.lastSeen).getTime();
          if (!isNaN(parsed)) {
            pingAgeSec = Math.max(0, (Date.now() - parsed) / 1000);
          }
        }

        // Check if car should move INSTANTLY to new coordinates:
        // 1. Driver has become or is RECENT (<2m), STALE (2-15m), or OFFLINE (>15m)
        // 2. Driver was previously RECENT, STALE, or OFFLINE before this update (e.g. reconnecting)
        // 3. Location update was not sent within regular ~5s live stream window (gap > 7.5s, e.g. 10s, 30s, 1m, 1h, 1d)
        // 4. GPS ping recorded timestamp is older than 12 seconds
        // 5. Positional jump distance > 300 meters (physically impossible in a normal 5s driving step)
        const shouldMoveInstant =
          isNonLiveStatus ||
          wasNonLive ||
          timeSinceLastUpdateMs > 7500 ||
          pingAgeSec > 12 ||
          remainingDistance > 300;

        if (remainingDistance > 0.5) {
          if (shouldMoveInstant) {
            // Cancel any in-flight slide animation
            if ((marker as any)._slideAnimId) {
              cancelAnimationFrame((marker as any)._slideAnimId);
              (marker as any)._slideAnimId = null;
            }

            // Move car INSTANTLY to new coordinates
            marker.setLatLng([targetLat, targetLon]);

            // Determine vehicle heading instantly
            let targetHeading = 0;
            const hasExplicitDirection =
              driver.direction != null &&
              !isNaN(Number(driver.direction)) &&
              Number(driver.direction) !== 0;

            if (hasExplicitDirection) {
              targetHeading = Number(driver.direction);
            } else if (remainingDistance > 2) {
              targetHeading = calculateBearing(currentPos.lat, currentPos.lng, targetLat, targetLon);
            } else {
              targetHeading = (marker as any)._visualHeading || driverHeadingsRef.current.get(key) || 0;
            }

            (marker as any)._visualHeading = targetHeading;
            driverHeadingsRef.current.set(key, targetHeading);

            (marker as any)._lastUpdateTime = now;
            (marker as any)._lastCoords = [targetLat, targetLon];
            (marker as any)._lastFreshnessStatus = freshness.status;
            const initialSpeedMps =
              driver.speed != null && !isNaN(Number(driver.speed)) && Number(driver.speed) > 0
                ? (Number(driver.speed) * 1000) / 3600
                : 10.5;
            (marker as any)._smoothedSpeed = initialSpeedMps;

            // Pan camera if selected
            if (isSelected && map.current) {
              const bounds = map.current.getBounds();
              if (!bounds.pad(-0.25).contains([targetLat, targetLon])) {
                lastPanTimeRef.current = Date.now();
                map.current.panTo([targetLat, targetLon], { animate: true, duration: 0.5 });
              }
            }
          } else {
            // Normal LIVE 5-second stream: smooth constant-speed slide
            // 1. Determine vehicle ground speed (meters/second)
            let measuredSpeedMps = 0;
            if (driver.speed != null && !isNaN(Number(driver.speed)) && Number(driver.speed) > 0) {
              measuredSpeedMps = (Number(driver.speed) * 1000) / 3600;
            } else if (lastCoords && lastUpdate) {
              const stepDist = haversineDistanceMeters(
                lastCoords[0],
                lastCoords[1],
                targetLat,
                targetLon
              );
              const dt = (now - lastUpdate) / 1000;
              if (dt >= 1.0 && dt <= 7.5) {
                measuredSpeedMps = stepDist / dt;
              }
            }

            if (measuredSpeedMps <= 0.5) {
              measuredSpeedMps = (marker as any)._smoothedSpeed || 10.5;
            }

            // 2. Smooth speed using EWMA (0.75 / 0.25) to prevent sudden speed jerks or oscillations
            const prevSpeed = (marker as any)._smoothedSpeed || measuredSpeedMps;
            const smoothedSpeed = prevSpeed * 0.75 + measuredSpeedMps * 0.25;
            (marker as any)._smoothedSpeed = smoothedSpeed;
            (marker as any)._lastUpdateTime = now;
            (marker as any)._lastCoords = [targetLat, targetLon];
            (marker as any)._lastFreshnessStatus = freshness.status;

            // 3. Determine vehicle heading
            let targetHeading = 0;
            const hasExplicitDirection =
              driver.direction != null &&
              !isNaN(Number(driver.direction)) &&
              Number(driver.direction) !== 0;

            if (hasExplicitDirection) {
              targetHeading = Number(driver.direction);
            } else {
              targetHeading = calculateBearing(currentPos.lat, currentPos.lng, targetLat, targetLon);
            }

            // Shortest angular turn to prevent 360-degree spins
            const currentVisual = (marker as any)._visualHeading != null ? (marker as any)._visualHeading : targetHeading;
            const shortestHeading = getShortestAngle(currentVisual, targetHeading);
            (marker as any)._visualHeading = shortestHeading;
            driverHeadingsRef.current.set(key, shortestHeading);

            // 4. Exact speed-consistent duration matching 5s GPS interval
            let animDuration = (remainingDistance / smoothedSpeed) * 1000;
            animDuration = Math.min(Math.max(1000, animDuration), 5500);

            slideMarker(marker, [currentPos.lat, currentPos.lng], [targetLat, targetLon], animDuration);

            // If this is the selected driver, smoothly follow with camera when nearing viewport boundary
            if (isSelected && map.current) {
              const nowTime = Date.now();
              if (nowTime - lastPanTimeRef.current > 1200) {
                const bounds = map.current.getBounds();
                if (!bounds.pad(-0.25).contains([targetLat, targetLon])) {
                  lastPanTimeRef.current = nowTime;
                  map.current.panTo([targetLat, targetLon], { animate: true, duration: 0.8 });
                }
              }
            }
          }
        } else {
          // Stationary (distance <= 0.5m)
          (marker as any)._lastUpdateTime = now;
          (marker as any)._lastCoords = [targetLat, targetLon];
          (marker as any)._lastFreshnessStatus = freshness.status;
          if (shouldMoveInstant && (marker as any)._slideAnimId) {
            cancelAnimationFrame((marker as any)._slideAnimId);
            (marker as any)._slideAnimId = null;
            marker.setLatLng([targetLat, targetLon]);
          }
        }

        // Fast in-place DOM update instead of costly marker.setIcon() recreate
        const el = marker.getElement();
        if (el) {
          el.style.cursor = "pointer";
          el.onclick = (e) => {
            e.stopPropagation();
            handleDriverMarkerClickRef.current((marker as any)._driver || driver);
          };
          const carContainer = el.querySelector(".driver-car-container") as HTMLElement | null;
          if (carContainer) {
            const currentAngle = (marker as any)._visualHeading != null ? (marker as any)._visualHeading : (driverHeadingsRef.current.get(key) || 0);
            carContainer.style.transform = `rotate(${currentAngle}deg)`;
          }
          const node = el.querySelector(".driver-marker-node");
          if (node) {
            node.classList.toggle("is-selected", !!isSelected);
          }
          const dot = el.querySelector(".status-dot") as HTMLElement | null;
          if (dot) {
            const freshness = getDriverFreshness(driver);
            dot.className = `status-dot ${freshness.status.toLowerCase()}`;
            dot.style.backgroundColor = freshness.dotColor;
          }
          const badge = el.querySelector(".driver-marker-badge") as HTMLElement | null;
          if (badge) {
            const freshness = getDriverFreshness(driver);
            badge.title = `${driver.code || driver.name || "Driver"} • ${freshness.label} (${freshness.timeAgoText})`;
          }
          const badgeText = el.querySelector(".badge-text");
          const labelText = driver.code || driver.name || "Driver";
          if (badgeText && badgeText.textContent !== labelText) {
            badgeText.textContent = labelText;
          }
        } else {
          // Fallback only if element is not rendered yet
          const heading = driverHeadingsRef.current.get(key) || 0;
          marker.setIcon(createDriverIcon(driver, !!isSelected, heading));
        }

        const expectedZIndex = isSelected ? 1500 : 1000;
        if ((marker as any)._currentZIndex !== expectedZIndex) {
          (marker as any)._currentZIndex = expectedZIndex;
          marker.setZIndexOffset(expectedZIndex);
        }
      }
    });

    // Remove any markers for drivers no longer in driversToDisplay
    driverMarkersRef.current.forEach((marker, key) => {
      if (!currentKeys.has(key)) {
        if ((marker as any)._slideAnimId) {
          cancelAnimationFrame((marker as any)._slideAnimId);
        }
        driversGroupRef.current?.removeLayer(marker);
        driverMarkersRef.current.delete(key);
        driverHeadingsRef.current.delete(key);
      }
    });
  }, [driversToDisplay, selectedDriver, mapReady]);

  // Cleanup driver marker animations on unmount
  useEffect(() => {
    return () => {
      driverMarkersRef.current.forEach((marker) => {
        if ((marker as any)._slideAnimId) {
          cancelAnimationFrame((marker as any)._slideAnimId);
        }
      });
      driverMarkersRef.current.clear();
      driverHeadingsRef.current.clear();
    };
  }, []);

  // Fetch Route Data for Selected Driver (only when switching selected driver, not on every coordinate tick)
  useEffect(() => {
    const fetchRouteData = async () => {
      if (
        !selectedDriver ||
        selectedDriver.lat === 0 ||
        selectedDriver.lon === 0
      ) {
        setRouteData(null);
        return;
      }

      // Avoid refetching route if we already fetched it for this driver
      if (lastFetchedRouteDriverIdRef.current === selectedDriver.id && routeData) {
        return;
      }

      try {
        lastFetchedRouteDriverIdRef.current = selectedDriver.id;
        const response = await fetch3(
          `${url}/api/drs/${selectedDriver.id}/sort-by?lat=${selectedDriver.lat}&long=${selectedDriver.lon}&updateLocation=false`,
          "get",
        );

        if (response.statusMessage !== "OK")
          throw new Error("Failed to fetch route");
        setRouteData(response.data);
      } catch (error) {
        console.error("Error fetching route data:", error);
        setRouteData(null);
      }
    };

    if (selectedDriver && showDriverPanel) {
      fetchRouteData();
    } else if (!selectedDriver || !showDriverPanel) {
      lastFetchedRouteDriverIdRef.current = null;
      setRouteData(null);
    }
  }, [selectedDriver?.id, showDriverPanel]);

  // Clear route polylines and shipments
  const clearRouteOverlays = () => {
    if (routeGroupRef.current) {
      routeGroupRef.current.clearLayers();
    }
    if (shipmentsGroupRef.current) {
      shipmentsGroupRef.current.clearLayers();
    }
    if (map.current) {
      map.current.closePopup();
    }
  };

  // Render Route and Shipment markers
  useEffect(() => {
    if (
      !map.current ||
      !mapReady ||
      !routeData ||
      !showDriverPanel ||
      !routeGroupRef.current ||
      !shipmentsGroupRef.current
    ) {
      return;
    }

    clearRouteOverlays();

    const rawCoords = routeData.fullRoutePolyline || [];
    const latLngs: [number, number][] = rawCoords
      .filter((coord: any) => Array.isArray(coord) && coord.length >= 2)
      .map((coord: [number, number]) => [coord[1], coord[0]]);

    if (latLngs.length >= 2) {
      const mainRoad = L.polyline(latLngs, {
        color: "#939EAC",
        weight: 8,
        opacity: 1,
        lineCap: "round",
        lineJoin: "round",
      });

      const dashLine = L.polyline(latLngs, {
        color: "#FFFFFF",
        weight: 3,
        opacity: 0.95,
        dashArray: "8, 12",
        lineCap: "round",
        lineJoin: "round",
      });

      routeGroupRef.current.addLayer(mainRoad);
      routeGroupRef.current.addLayer(dashLine);

      const bounds = L.latLngBounds(latLngs);
      if (selectedDriver?.lat && selectedDriver?.lon) {
        bounds.extend([selectedDriver.lat, selectedDriver.lon]);
      }
      if (bounds.isValid()) {
        map.current.fitBounds(bounds, {
          padding: [100, 100],
        });
      }
    }

    // Render Shipment markers along route
    (routeData.shipments || []).forEach((shipment: any) => {
      const lat = shipment.receiverLatitude || shipment.lat;
      const lng = shipment.receiverLongitude || shipment.lng;
      if (!lat || !lng) return;

      const getIconUrl = () => {
        if (shipment.status === "DELIVERED") {
          return packageDeliveredIcon;
        } else if (shipment.status === "NOT_DELIVERED") {
          return packageNotDeliveredIcon;
        } else {
          return packagePendingOFDIcon;
        }
      };

      const iconUrl = getIconUrl();

      const shipmentIcon = L.icon({
        iconUrl,
        iconSize: [42, 50],
        iconAnchor: [21, 46],
        popupAnchor: [0, -42],
      });

      const marker = L.marker([Number(lat), Number(lng)], {
        icon: shipmentIcon,
        zIndexOffset: 700,
      });

      const awbNumber =
        shipment.awbNumber || shipment.awb || shipment.trackingNumber || "";
      const cleanAWB = awbNumber.toString().trim();

      const popupHtml = `
        <div class="custom-awb-bubble">
          <img 
            src="${iconUrl}" 
            alt="Package" 
            style="width: 28px; height: 28px; margin-right: 8px; flex-shrink: 0;" 
          />
          <span style="font-size: 16px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.5px;">
            #${cleanAWB}
          </span>
        </div>
      `;

      marker.bindPopup(popupHtml, {
        closeButton: false,
        className: "custom-awb-popup-wrapper",
        offset: [0, -8],
      });

      marker.on("click", () => {
        if (map.current) {
          map.current.setView([Number(lat), Number(lng)], 18, {
            animate: true,
          });
        }
      });

      shipmentsGroupRef.current?.addLayer(marker);
    });
  }, [routeData, mapReady, showDriverPanel]);




  return (
    <>
      <style>{`
        @keyframes slideUp {
          from {
            transform: translate(-50%, 100%);
            opacity: 0;
          }
          to {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        /* Custom Leaflet AWB Pill Popup Styling */
        .custom-awb-popup-wrapper .leaflet-popup-content-wrapper {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
          border: none !important;
        }
        .custom-awb-popup-wrapper .leaflet-popup-content {
          margin: 0 !important;
          line-height: 1 !important;
        }
        .custom-awb-popup-wrapper .leaflet-popup-tip-container {
          display: none !important;
        }
        .custom-awb-bubble {
          background: white;
          border-radius: 9999px;
          padding: 6px 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          border: 1.5px solid #E5E7EB;
          min-width: 150px;
          max-width: 280px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Driver Marker Custom Styles */
        .driver-marker-wrapper {
          background: transparent !important;
          border: none !important;
          cursor: pointer !important;
        }
        .driver-marker-node {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          cursor: pointer !important;
          pointer-events: auto !important;
        }
        .driver-marker-badge {
          position: absolute;
          top: -8px;
          background: rgba(17, 24, 39, 0.90);
          backdrop-filter: blur(4px);
          color: #ffffff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.3px;
          padding: 2px 7px;
          border-radius: 9999px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
          cursor: pointer !important;
          pointer-events: auto !important;
          z-index: 20;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.2s ease;
        }
        .driver-marker-node.is-selected .driver-marker-badge {
          background: #2563eb;
          border-color: #93c5fd;
          box-shadow: 0 0 12px rgba(37, 99, 235, 0.7);
        }
        .status-dot {
          width: 7px;
          height: 7px;
          border-radius: 9999px;
          display: inline-block;
          flex-shrink: 0;
          pointer-events: none;
          transition: background-color 0.3s ease;
        }
        .status-dot.live, .status-dot.online {
          background: #22c55e;
          box-shadow: 0 0 6px #22c55e;
        }
        .status-dot.recent {
          background: #0ea5e9;
          box-shadow: 0 0 5px #0ea5e9;
        }
        .status-dot.stale {
          background: #f59e0b;
          box-shadow: 0 0 5px #f59e0b;
        }
        .status-dot.offline {
          background: #94a3b8;
        }
        .driver-car-container {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
          will-change: transform;
          cursor: pointer !important;
          pointer-events: auto !important;
        }
        .driver-car-img {
          width: 42px;
          height: 42px;
          object-fit: contain;
          filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.35));
          transform: rotate(-35deg);
          cursor: pointer !important;
          pointer-events: auto !important;
        }
        .driver-marker-node.is-selected::after {
          content: '';
          position: absolute;
          width: 50px;
          height: 50px;
          border-radius: 9999px;
          border: 2.5px solid #3b82f6;
          animation: markerPulse 1.8s infinite;
          pointer-events: none;
        }
        @keyframes markerPulse {
          0% {
            transform: scale(0.85);
            opacity: 1;
          }
          100% {
            transform: scale(1.45);
            opacity: 0;
          }
        }

        /* Smooth tile scaling & transitions like Google Maps */
        .leaflet-tile {
          will-change: transform, opacity;
        }
        .leaflet-zoom-anim .leaflet-zoom-animated {
          transition: transform 0.28s cubic-bezier(0.25, 0.1, 0.25, 1) !important;
        }
      `}</style>
      <div className="rounded-3xl bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] p-6 border border-gray-200/50 space-y-2 relative z-0 isolate">
        <div className="flex-wrap lg:flex-nowrap flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setSearchParams({ tab: "Overview" });
              }}
              className="p-2 border rounded-full hover:bg-gray-100 transition"
            >
              <IconChevronLeft size={20} />
            </button>
            <div className="flex items-center bg-white/95 px-2 py-2 rounded-full shadow-md">
              <span
                className={`w-5 h-5 rounded-full ${
                  socketConnected ? "bg-green-500 animate-pulse" : "bg-red-500"
                }`}
              ></span>
            </div>
            <div className="bg-white border-[0.5px] border-[#D2D2D2] shadow-md rounded-md p-2 flex items-center h-[58px]">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-xl font-bold text-orange-600 leading-tight">
                    {ofdData?.outForDelivery || 0}
                  </p>
                  <p className="text-xs text-gray-600 leading-tight">OFD</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-green-600 leading-tight">
                    {ofdData?.ordersDelivered || 0}
                  </p>
                  <p className="text-xs text-gray-600 leading-tight">DLVD</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-red-600 leading-tight">
                    {ofdData?.ordersNotDelivered || 0}
                  </p>
                  <p className="text-xs text-gray-600 leading-tight">N-DLVD</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-600 leading-tight">
                    {driverStatusCounts.ACTIVE}
                  </p>
                  <p className="text-xs text-gray-600 leading-tight whitespace-nowrap">
                    Active Drivers
                  </p>
                </div>
              </div>
            </div>

            {/* Real-time Driver Freshness Status Breakdown (Styled like OFD card, click to filter map) */}
            <div className="hidden sm:flex items-center bg-white border-[0.5px] border-[#D2D2D2] shadow-md rounded-md p-2 h-[58px]">
              <div className="grid grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => toggleStatusFilter("LIVE")}
                  className={`text-center px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    selectedStatuses.includes("LIVE")
                      ? "bg-emerald-50 ring-2 ring-emerald-500 shadow-sm opacity-100"
                      : "hover:bg-emerald-50/40 opacity-40 hover:opacity-80"
                  }`}
                  title="Toggle: Live drivers (<30s)"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xl font-bold text-emerald-600 leading-tight">
                      {driverStatusCounts.LIVE}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium whitespace-nowrap leading-tight">
                    Live <span className="text-[10px] text-gray-400 font-normal">(&lt;30s)</span>
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => toggleStatusFilter("RECENT")}
                  className={`text-center px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    selectedStatuses.includes("RECENT")
                      ? "bg-sky-50 ring-2 ring-sky-500 shadow-sm opacity-100"
                      : "hover:bg-sky-50/40 opacity-40 hover:opacity-80"
                  }`}
                  title="Toggle: Recent drivers (<2m)"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-sky-500"></span>
                    <span className="text-xl font-bold text-sky-600 leading-tight">
                      {driverStatusCounts.RECENT}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium whitespace-nowrap leading-tight">
                    Recent <span className="text-[10px] text-gray-400 font-normal">(&lt;2m)</span>
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => toggleStatusFilter("STALE")}
                  className={`text-center px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    selectedStatuses.includes("STALE")
                      ? "bg-amber-50 ring-2 ring-amber-500 shadow-sm opacity-100"
                      : "hover:bg-amber-50/40 opacity-40 hover:opacity-80"
                  }`}
                  title="Toggle: Stale drivers (2-15m)"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="text-xl font-bold text-amber-600 leading-tight">
                      {driverStatusCounts.STALE}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium whitespace-nowrap leading-tight">
                    Stale <span className="text-[10px] text-gray-400 font-normal">(2-15m)</span>
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => toggleStatusFilter("OFFLINE")}
                  className={`text-center px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    selectedStatuses.includes("OFFLINE")
                      ? "bg-slate-100 ring-2 ring-slate-400 shadow-sm opacity-100"
                      : "hover:bg-slate-100/50 opacity-40 hover:opacity-80"
                  }`}
                  title="Toggle: Offline drivers (>15m)"
                >
                  <div className="flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                    <span className="text-xl font-bold text-slate-500 leading-tight">
                      {driverStatusCounts.OFFLINE}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium whitespace-nowrap leading-tight">
                    Offline <span className="text-[10px] text-gray-400 font-normal">(&gt;15m)</span>
                  </p>
                </button>
              </div>

              {!isAllStatusesSelected ? (
                <>
                  <div className="h-6 w-[1px] bg-gray-200 mx-1"></div>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedStatuses(["LIVE", "RECENT", "STALE", "OFFLINE"])
                    }
                    className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md text-xs font-semibold transition-all cursor-pointer shadow-xs hover:shadow active:scale-95 whitespace-nowrap"
                    title="Select all & show all drivers"
                  >
                    <span>Show All</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="h-6 w-[1px] bg-gray-200 mx-1"></div>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedStatuses(["LIVE", "RECENT", "STALE"])
                    }
                    className="flex items-center gap-1 px-2.5 py-1 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-md text-xs font-semibold transition-all cursor-pointer shadow-xs hover:shadow active:scale-95 whitespace-nowrap"
                    title="Hide offline drivers"
                  >
                    <IconX size={13} stroke={2.5} className="text-gray-500" />
                    <span>Hide Offline</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 py-4 lg:py-0">
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {(searchLoading ||
                (isSearching &&
                  searchQuery.trim() &&
                  searchResults.length === 0)) && (
                <div className="absolute right-9 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              <input
                className="pl-11 pr-12 py-2.5 w-80 rounded-full border border-gray-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-sm font-medium shadow-sm transition-all"
                placeholder="Search driver name or code to navigate..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() =>
                  searchResults.length > 0 && setShowDropdown(true)
                }
                onBlur={(e) => {
                  const related = e.relatedTarget as HTMLElement;
                  if (
                    !related ||
                    !e.currentTarget.parentElement?.contains(related)
                  ) {
                    setTimeout(() => setShowDropdown(false), 150);
                  }
                }}
              />
              {searchQuery.trim() && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <IconX />
                </button>
              )}
              {showDropdown &&
                (searchResults.length > 0 ||
                  (isSearching && searchQuery.trim())) && (
                  <div
                    className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-[2000] max-h-80 overflow-y-auto"
                    style={{ zIndex: 2000 }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className="p-2">
                      {searchLoading && searchResults.length === 0 ? (
                        <div className="flex items-center justify-center py-8 text-center">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-xs text-gray-500">
                            Searching drivers...
                          </p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((driver, index) => (
                          <div
                            key={driver.id || driver.code || index}
                            className={`flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer rounded-lg transition-colors ${
                              index === selectedIndex
                                ? "bg-blue-50 border border-blue-200"
                                : "hover:bg-gray-50"
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDriverSelectFromSearch(driver);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDriverSelectFromSearch(driver);
                            }}
                            onMouseEnter={() => setSelectedIndex(index)}
                          >
                            {(() => {
                              const dropdownAvatar =
                                getDriverAvatar(driver) ||
                                (driver.image && typeof driver.image === "string" && driver.image.startsWith("http")
                                  ? driver.image
                                  : "");
                              return dropdownAvatar ? (
                                <img
                                  src={dropdownAvatar}
                                  alt={driver.name}
                                  className="w-10 h-10 rounded-full border-2 border-gray-200 object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full border-2 border-gray-200 bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold flex items-center justify-center text-xs shadow-sm">
                                  {(driver.name || "D")
                                    .split(" ")
                                    .map((n) => n[0])
                                    .slice(0, 2)
                                    .join("")
                                    .toUpperCase()}
                                </div>
                              );
                            })()}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-gray-900 text-sm">
                                  {driver.name}
                                </h4>
                                {(() => {
                                  const freshness = getDriverFreshness(driver);
                                  return (
                                    <span
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${freshness.badgeClass}`}
                                    >
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${freshness.dotClass}`}
                                      />
                                      {freshness.status}
                                    </span>
                                  );
                                })()}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-blue-600 font-medium">
                                  #{driver.code}
                                </span>
                                {(() => {
                                  const freshness = getDriverFreshness(driver);
                                  return (
                                    <span className="text-xs text-gray-500 font-medium">
                                      • {freshness.timeAgoText}
                                    </span>
                                  );
                                })()}
                                {driver.speed != null && Number(driver.speed) > 0 && (
                                  <span className="text-xs text-gray-500 font-medium">
                                    • {Math.round(Number(driver.speed))} km/h
                                  </span>
                                )}
                              </div>
                              {driver.currentLocation && (
                                <p className="text-xs text-gray-500 mt-1 break-all line-clamp-1">
                                  {driver.currentLocation}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <svg
                                className="w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        ))
                      ) : null}
                    </div>
                    <div className="border-t p-2">
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearSearch();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSearch();
                        }}
                        className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-1 cursor-pointer"
                      >
                        Clear Search
                      </button>
                    </div>
                  </div>
                )}
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-600">EN</span>
              <button
                type="button"
                onClick={() =>
                  setLanguage((prev) => (prev === "en" ? "ar" : "en"))
                }
                className="relative w-14 h-8 rounded-full bg-gray-300 shadow-inner transition-colors duration-300 focus:outline-none"
                style={{
                  backgroundColor: language === "ar" ? "#65C466" : "#E9E9EA",
                }}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                    language === "ar" ? "translate-x-6" : ""
                  }`}
                />
              </button>
              <span className="text-xs font-medium text-gray-600">AR</span>
            </div>
          </div>
        </div>

        <div className="h-[calc(100vh-15.5rem)] rounded-2xl overflow-hidden shadow-md relative z-0 isolate">
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg font-semibold text-gray-700">
                  Loading drivers...
                </p>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
              <p className="text-lg font-semibold text-red-600">
                Failed to load driver data
              </p>
            </div>
          )}
          {mapLoadError && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
              <div className="text-center">
                <p className="text-lg font-semibold text-red-600 mb-2">
                  {mapLoadError}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
          {/* Map Layer / Satellite Selector Dropdown (Same as Last-Mile AddRouteMap) */}
          <div
            ref={langDropdownRef}
            className="absolute top-4 left-4 z-[500]"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => setLangDropdownOpen((prev) => !prev)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white border border-gray-200 rounded-xl shadow-md text-xs font-semibold text-gray-700 transition-all cursor-pointer backdrop-blur-sm"
                title="Change Map Layer / Satellite"
              >
                <IconLanguage size={18} className="text-[#FBD037]" />
                <span>{TILE_LAYERS[language]?.name || "English"}</span>
                <IconChevronDown
                  size={14}
                  className={`text-gray-500 transition-transform duration-200 ${
                    langDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {langDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-52 bg-white border border-gray-100 rounded-xl shadow-xl z-[600] p-1.5 flex flex-col gap-1">
                  <div className="px-2 py-1 text-[10px] font-bold tracking-wider text-gray-400 uppercase border-b border-gray-100">
                    Select Map Layer
                  </div>
                  {(Object.keys(TILE_LAYERS) as MapLanguageOption[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setLanguage(key);
                        setLangDropdownOpen(false);
                      }}
                      className={`flex items-center justify-between w-full px-2.5 py-2 text-xs rounded-lg transition-colors text-left font-medium cursor-pointer ${
                        language === key
                          ? "bg-[#FBD037]/20 text-gray-900 font-bold"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <span>{TILE_LAYERS[key].label}</span>
                      {language === key && (
                        <span className="w-2 h-2 rounded-full bg-[#FBD037]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>


          {/* Fit all drivers button */}
          <button
            type="button"
            onClick={fitAllDrivers}
            className="absolute bottom-20 right-3 z-[500] p-2 bg-white/95 hover:bg-white text-gray-700 rounded-lg shadow-md border border-gray-200 transition-all backdrop-blur-sm cursor-pointer hover:text-blue-600"
            title="Fit all drivers on map"
          >
            <IconMaximize size={18} />
          </button>

          {/* Leaflet Map DOM Container */}
          <div ref={mapContainer} className="w-full h-full" />

          {/* Persistent Driver Details Bottom Card - Rendered directly to prevent unmount and flickering */}
          {showDriverPanel && selectedDriver && (
            <div
              key="driver-details-panel-container"
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-full bg-white rounded-xl shadow-2xl border border-gray-200 z-[2000] p-4 space-y-2 transition-transform duration-200"
              style={{ zIndex: 2000 }}
            >
              <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    {(() => {
                      const cardAvatar =
                        getDriverAvatar(selectedDriver) ||
                        (selectedDriver.image &&
                        typeof selectedDriver.image === "string" &&
                        selectedDriver.image.startsWith("http")
                          ? selectedDriver.image
                          : "");

                      if (cardAvatar && !avatarError) {
                        return (
                          <img
                            src={cardAvatar}
                            alt={selectedDriver.name}
                            onError={() => setAvatarError(true)}
                            className="w-12 h-12 rounded-full border-2 border-blue-500 object-cover"
                          />
                        );
                      }
                      return (
                        <div className="w-12 h-12 rounded-full border-2 border-blue-500 bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                          {(selectedDriver.name || "D")
                            .split(" ")
                            .map((n) => n[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </div>
                      );
                    })()}
                    <div
                      className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white"
                      style={{
                        backgroundColor: getDriverFreshness(selectedDriver).dotColor,
                      }}
                      title={getDriverFreshness(selectedDriver).label}
                    ></div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5">
                      <h3 className="text-lg font-bold text-gray-900 leading-tight">
                        {selectedDriver.name}
                      </h3>
                      <span className="text-sm font-semibold text-blue-600">
                        #{selectedDriver.code}
                      </span>
                      {(() => {
                        const freshness = getDriverFreshness(selectedDriver);
                        return (
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border shadow-sm ${freshness.badgeClass}`}
                          >
                            <span
                              className={`w-2 h-2 rounded-full ${freshness.dotClass}`}
                            />
                            {freshness.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="max-w-xs truncate">{selectedDriver.currentLocation || "Jaipur"}</span>
                      {selectedDriver.lastSeen && (
                        <span>
                          • Last ping:{" "}
                          <strong className="text-gray-700">
                            {new Date(selectedDriver.lastSeen).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })}
                          </strong>{" "}
                          ({getDriverFreshness(selectedDriver).timeAgoText})
                        </span>
                      )}
                      {selectedDriver.speed != null && Number(selectedDriver.speed) > 0 && (
                        <span>
                          • Speed:{" "}
                          <strong className="text-gray-700">
                            {Math.round(Number(selectedDriver.speed))} km/h
                          </strong>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 ml-6">
                    <div className="text-center">
                      <p className="text-xl font-bold text-blue-600">
                        {selectedDriver.totalShipments || 0}
                      </p>
                      <span className="text-xs font-medium text-blue-700">Total</span>
                    </div>

                    <div className="text-center">
                      <p className="text-xl font-bold text-orange-600">
                        {selectedDriver.outForDelivery || 0}
                      </p>
                      <span className="text-xs font-medium text-orange-700">OFD</span>
                    </div>

                    <div className="text-center">
                      <p className="text-xl font-bold text-green-600">
                        {selectedDriver.ordersDelivered || 0}
                      </p>
                      <span className="text-xs font-medium text-green-700">DLVD</span>
                    </div>

                    <div className="text-center">
                      <p className="text-xl font-bold text-red-600">
                        {selectedDriver.ordersNotDelivered || 0}
                      </p>
                      <span className="text-xs font-medium text-red-700">N-DLVD</span>
                    </div>
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDriverPanel(false);
                      setSelectedDriver(null);
                      setIsSelectionLocked(false);
                      clearRouteOverlays();
                    }}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                    title="Close details"
                  >
                    <IconX size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
